import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { useImportDataOptions } from "@/hooks/use-import-data.hook.ts";
import { useImportDataEditContext } from "@/pages/settings/import-data/edit/import-data-edit.types.ts";
import { ImportDataFieldPreviewModal } from "@/pages/settings/import-data/widgets/ImportDataFieldPreviewModal.tsx";
import { useImportDataParseByType, useImportDataStart } from "@/queries/import-data.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiImportDataType,
  ApiImportFieldUpdateBehavior,
  ApiImportNewValueBehavior,
} from "@/types/import-data.types.ts";
import {
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Table,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface ColumnWithSamples {
  column_index: number;
  header: string;
  import_type: ApiImportDataType;
  as_field: string;
  update_behavior: ApiImportFieldUpdateBehavior;
  sample_values: string[];
  create_new_values: boolean;
  count_values_to_create: number;
}

export function ImportDataEditStepConfirmation() {
  const {
    importId,
    goToNextStep,
    registerOnNext,
    importPayload,
    setNextLoading,
    nextButtonText,
    rowsCount,
    file,
    importType,
    selectedCustomTableId,
  } = useImportDataEditContext();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const { openModal } = useModals();
  const { data: parseOverview } = useImportDataParseByType(
    importId,
    importType,
    selectedCustomTableId
  );
  const { t } = useTranslation();
  const {
    getImportDataTypeOptions,
    getUpdateBehaviorOptions,
    getImportDataFieldTitle,
    getImportBehaviorOptions,
    getNewValuesBehaviorOptions,
  } = useImportDataOptions();
  const startMutation = useImportDataStart();
  const { onError } = useErrorHandle();

  const importTypeOptions = getImportDataTypeOptions();
  const updateBehaviorOptions = getUpdateBehaviorOptions();
  const importBehaviorOptions = getImportBehaviorOptions();
  const newValuesBehaviorOptions = getNewValuesBehaviorOptions();

  const selectedBehavior = importBehaviorOptions.find(
    (o) => o.id === importPayload?.import_behavior
  );

  const getImportTypeTitle = (type: ApiImportDataType, fieldKey?: string) => {
    const typeTitle = importTypeOptions.find((o) => o.id === type)?.title ?? type;
    if (type === ApiImportDataType.custom_table && fieldKey) {
      const table = parseOverview?.possible_tables?.find((t) =>
        t.fields.some((f) => f.field_key === fieldKey)
      );
      if (table) return `${typeTitle}: ${table.table_name}`;
    }
    return typeTitle;
  };

  const getUpdateBehaviorTitle = (behavior: ApiImportFieldUpdateBehavior) =>
    updateBehaviorOptions.find((o) => o.id === behavior)?.title ?? behavior;

  const findFieldEntity = (importType: ApiImportDataType, fieldKey: string) => {
    if (importType === ApiImportDataType.custom_table) {
      for (const table of parseOverview?.possible_tables ?? []) {
        const field = table.fields.find((f) => f.field_key === fieldKey);
        if (field) return field;
      }
      return undefined;
    }

    const possibleFields = parseOverview?.possible_fields;

    return possibleFields?.find((f) => f.field_key === fieldKey);
  };

  const payloadColumns = importPayload?.columns;
  const parseColumns = parseOverview?.columns;

  const columnsWithSamples = useMemo<ColumnWithSamples[]>(() => {
    if (!payloadColumns || !parseColumns) return [];
    return payloadColumns.map((col) => {
      const parseCol = parseColumns.find((c) => c.column_index === col.column_index);
      return {
        ...col,
        header: parseCol?.header ?? "",
        sample_values: parseCol?.sample_values ?? [],
        count_values_to_create: parseCol?.count_values_to_create ?? 0,
      };
    });
  }, [payloadColumns, parseColumns]);

  const getNewValuesBehaviorTitle = (createNew: boolean) => {
    const behavior = createNew
      ? ApiImportNewValueBehavior.create_new_values
      : ApiImportNewValueBehavior.skip_news_records;
    return newValuesBehaviorOptions.find((o) => o.id === behavior)?.title ?? "";
  };

  const tableColumns = useMemo<ColumnDef<ColumnWithSamples>[]>(
    () => [
      {
        id: "header",
        header: t("import_data:file_column"),
        accessorKey: "header",
        cell: (cell) => {
          const { column_index, header, sample_values } = cell.row.original;
          const isExpanded = expandedRows.has(column_index);
          return (
            <Stack className={"pr-4"}>
              <Text className={"text-title whitespace-nowrap"}>{header}</Text>
              {sample_values.length > 0 && (
                <Text
                  className={`text-secondary cursor-pointer ${!isExpanded ? "line-clamp-5" : ""}`}
                  onClick={() =>
                    setExpandedRows((prev) => {
                      const next = new Set(prev);
                      if (isExpanded) {
                        next.delete(column_index);
                      } else {
                        next.add(column_index);
                      }
                      return next;
                    })
                  }
                >
                  {t("import_data:samples", { list: sample_values.join(", ") })}
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        id: "as_field",
        header: t("import_data:destination_in"),
        accessorKey: "as_field",
        cell: (cell) => {
          const col = cell.row.original;
          const entity = findFieldEntity(col.import_type, col.as_field);
          const fieldTitle = entity ? getImportDataFieldTitle(col.import_type, entity) : col.header;
          return (
            <Stack className={"pr-4"}>
              <Text className={"text-title"}>{fieldTitle}</Text>
              <Text className={"text-secondary whitespace-nowrap"}>
                {getImportTypeTitle(col.import_type, col.as_field)}
              </Text>
            </Stack>
          );
        },
      },
      {
        id: "mode",
        header: t("import_data:import_mode"),
        cell: (cell) => {
          const col = cell.row.original;
          return (
            <Stack className={"pr-4"}>
              <Text className={"text-secondary whitespace-nowrap"}>
                {`${t("import_data:update_as")} ${getUpdateBehaviorTitle(col.update_behavior)}`}
              </Text>
              {col.count_values_to_create > 0 && (
                <Text className={"text-secondary whitespace-nowrap"}>
                  {getNewValuesBehaviorTitle(col.create_new_values)}
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        id: "actions",
        header: "",
        sticky: "right",
        cell: (cell) => {
          const col = cell.row.original;
          return (
            <Stack className={"justify-end"} gap={"sm"} direction={"horizontal"}>
              <ButtonIcon
                icon={<FontAwesomeIcon icon={"fa-light fa-eye"} />}
                variant={"secondary"}
                onClick={() => {
                  const parsedColumn = parseOverview?.columns.find(
                    (c) => c.column_index === col.column_index
                  );

                  if (!importId || !parsedColumn) return;

                  openModal(ImportDataFieldPreviewModal, {
                    parsedColumn,
                    selectedColumn: {
                      import_type: col.import_type,
                      as_field: col.as_field,
                    },
                    importId,
                    importType,
                  });
                }}
              />
            </Stack>
          );
        },
        ...ColumnSizes.actions,
      },
    ],
    [parseOverview, expandedRows]
  );

  const handleStartImport = async () => {
    if (!importId) return;
    setNextLoading(true);
    try {
      await startMutation.mutateAsync(importId);
      goToNextStep();
    } catch (error) {
      onError(error as Error);
    } finally {
      setNextLoading(false);
    }
  };

  useLayoutEffect(() => {
    registerOnNext(handleStartImport);
    return () => registerOnNext(null);
  }, [registerOnNext]);

  return (
    <Stack gap={"sm"}>
      <Stack className={"card-panel"}>
        <Title
          size={"lg"}
          header={t("import_data:ready_to_import")}
          caption={t("import_data:ready_to_import_caption")}
        />
      </Stack>
      <Stack className={"card-panel"} gap={"2xl"}>
        <Title size={"md"} header={t("import_data:file_info")} />
        <Stack>
          <ListItem title={t("common:file")} caption={file?.name} />
          <ListItem title={t("import_data:records")} caption={`${rowsCount}`} />
        </Stack>
      </Stack>
      <Stack className={"card-panel"} gap={"2xl"}>
        <Title size={"md"} header={t("import_data:update_mode")} />
        <Stack gap={"md"}>
          <ListItem
            emoji={":warning:"}
            title={selectedBehavior?.title}
            caption={selectedBehavior?.caption}
          />
          <ListItem
            behavior={"card"}
            title={t("import_data:match_by_field")}
            caption={
              parseOverview?.columns.find((c) => c.column_index === importPayload?.matching_by)
                ?.header ?? ""
            }
          />
        </Stack>
      </Stack>
      <Stack className={"card-panel"} gap={"2xl"}>
        <Title
          size={"md"}
          header={t("import_data:how_data_matched")}
          caption={t("import_data:count_columns_imported", {
            columns: t("plurals:columns", { count: columnsWithSamples.length }),
          })}
        />
        <Table data={columnsWithSamples} columns={tableColumns} stickyLastColumn={true} />
      </Stack>

      <Button size={"lg"} onClick={handleStartImport} isLoading={startMutation.isPending}>
        {nextButtonText}
      </Button>
    </Stack>
  );
}
